'use client';

import { CheckIcon, MinusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* ───────────────────── Constants ───────────────────── */

const NAVBAR_HEIGHT = 48;
const HEADER_HEIGHT = 60;
const EDGE = 16;
const MIN_W = 320;
const MIN_H = 380;
const MAX_W = 900; // floating only
const SNAP_PX = 80;
const DETACH_PX = 0;
const FLOAT_MAX_VH = 0.8;

/* ───────────────────── Helpers ───────────────────── */

const DockIcon = ({ docked, ...props }) => (
    <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
        {...props}
    >
        <rect
            x="3"
            y="3"
            width="14"
            height="14"
            rx="2"
            ry="2"
            strokeWidth="1.5"
        />
        {docked ? (
            <>
                <path
                    d="M11 7H7v4"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 12L7 7"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </>
        ) : (
            <>
                <path
                    d="M9 7h4M13 7v4"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M8 12L13 7"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </>
        )}
    </svg>
);

function useWindowSize() {
    const [s, set] = useState({ w: 0, h: 0 });
    useEffect(() => {
        const onR = () => set({ w: window.innerWidth, h: window.innerHeight });
        onR();
        window.addEventListener('resize', onR);
        return () => window.removeEventListener('resize', onR);
    }, []);
    return s;
}

function clampFloatRect({ x, y, width, height }, w, h, minimized) {
    const vhCap = h * FLOAT_MAX_VH;
    const maxW = Math.min(MAX_W, Math.max(MIN_W, w - EDGE * 2));
    const maxHBase = h - NAVBAR_HEIGHT - EDGE * 2;
    const maxH = Math.min(
        minimized ? HEADER_HEIGHT : Math.max(MIN_H, maxHBase),
        vhCap
    );

    const minH = minimized ? HEADER_HEIGHT : MIN_H;
    const cw = Math.min(Math.max(width, MIN_W), maxW);
    const ch = Math.min(Math.max(height, minH), maxH);

    const minX = EDGE;
    const maxX = Math.max(EDGE, w - cw - EDGE);
    const minY = NAVBAR_HEIGHT + EDGE;
    const maxY = Math.max(NAVBAR_HEIGHT + EDGE, h - ch - EDGE);

    return {
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY),
        width: cw,
        height: ch,
    };
}

function readLS(k, fb) {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fb;
    } catch {
        return fb;
    }
}
function writeLS(k, v) {
    try {
        localStorage.setItem(k, JSON.stringify(v));
    } catch {
        // ignore
    }
}

/* ───────────────────── Resize hook ───────────────────── */

function useResize({ getRect, setRect, docked, minimized, onResizeEnd }) {
    const ref = useRef(null);
    const shieldRef = useRef(null);

    useEffect(() => {
        const onMove = (e) => {
            if (!ref.current) return;
            const { edge, startX, startY, startRect } = ref.current;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;

            let { x, y, width, height } = startRect;

            if (docked && edge === 'w') {
                const right = startRect.x + startRect.width;
                let newX = startRect.x + dx;
                const minX = EDGE;
                const maxX = right - MIN_W;
                newX = Math.min(Math.max(newX, minX), maxX);

                x = newX;
                width = right - newX;
                y = startRect.y;
                height = startRect.height;
            } else if (!docked) {
                const vw =
                    typeof window !== 'undefined'
                        ? window.innerWidth
                        : startRect.x + startRect.width;
                const vh =
                    typeof window !== 'undefined'
                        ? window.innerHeight
                        : startRect.y + startRect.height;

                const minH = minimized ? HEADER_HEIGHT : MIN_H;
                const right = startRect.x + startRect.width;
                const bottom = startRect.y + startRect.height;

                if (edge.includes('e')) {
                    const maxWidth = Math.min(MAX_W, vw - EDGE - startRect.x);
                    let newWidth = startRect.width + dx;
                    width = Math.min(Math.max(newWidth, MIN_W), maxWidth);
                } else if (edge.includes('w')) {
                    let newX = startRect.x + dx;
                    const minX = EDGE;
                    const maxX = right - MIN_W;
                    newX = Math.min(Math.max(newX, minX), maxX);
                    width = right - newX;
                    x = newX;
                }

                if (edge.includes('s')) {
                    const maxHeight = Math.min(
                        vh * FLOAT_MAX_VH,
                        vh - EDGE - startRect.y
                    );
                    let newHeight = startRect.height + dy;
                    height = Math.min(Math.max(newHeight, minH), maxHeight);
                } else if (edge.includes('n')) {
                    let newY = startRect.y + dy;
                    const minY = NAVBAR_HEIGHT + EDGE;
                    const maxY = bottom - minH;
                    newY = Math.min(Math.max(newY, minY), maxY);
                    height = bottom - newY;
                    y = newY;
                }
            } else {
                return;
            }

            setRect({ x, y, width, height });
        };

        const onUp = () => {
            if (ref.current) {
                onResizeEnd?.(ref.current.edge);
                ref.current = null;
            }
            if (shieldRef.current) {
                document.body.removeChild(shieldRef.current);
                shieldRef.current = null;
            }
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [setRect, docked, minimized, onResizeEnd]);

    const onDown = useCallback(
        (edge) => (e) => {
            e.preventDefault();
            const r = getRect();
            ref.current = {
                edge,
                startX: e.clientX,
                startY: e.clientY,
                startRect: r,
            };
            document.body.style.userSelect = 'none';

            let cursor = 'auto';
            if (edge === 'w') {
                cursor = 'ew-resize';
            } else if (edge === 'se' || edge === 'nw') {
                cursor = 'nwse-resize';
            } else if (edge === 'ne' || edge === 'sw') {
                cursor = 'nesw-resize';
            }
            document.body.style.cursor = cursor;

            if (typeof document !== 'undefined' && !shieldRef.current) {
                const shield = document.createElement('div');
                shield.style.position = 'fixed';
                shield.style.inset = '0';
                shield.style.zIndex = '10000';
                shield.style.pointerEvents = 'auto';
                shield.style.background = 'transparent';
                shieldRef.current = shield;
                document.body.appendChild(shield);
            }
        },
        [getRect]
    );

    return onDown;
}

/* ───────────────────── Component ───────────────────── */

export function EditorDrawer({
    title,
    open,
    onClose,
    onCancel,
    onSave,
    children,
    bodyRef,
    onDockChange,
    storageKey = 'EditorDrawer',
    hideFooter = false, // Added prop
}) {
    const win = useWindowSize();
    const [portal, setPortal] = useState(null);
    const defaultFloatRectRef = useRef({
        x: 0,
        y: NAVBAR_HEIGHT + 32,
        width: 448,
        height: 560,
    });

    const SK = useMemo(
        () => ({
            dock: `${storageKey}:docked`,
            min: `${storageKey}:min`,
            rect: `${storageKey}:floatRect`,
            dockWidth: `${storageKey}:dockWidth`,
        }),
        [storageKey]
    );

    const [docked, setDocked] = useState(readLS(SK.dock, false));
    const [minimized, setMinimized] = useState(readLS(SK.min, false));

    const [floatRect, setFloatRect] = useState(() =>
        readLS(SK.rect, defaultFloatRectRef.current)
    );
    const [dockWidth, setDockWidth] = useState(readLS(SK.dockWidth, 448));

    const [rectState, setRectState] = useState(floatRect);

    const getRect = useCallback(() => rectState, [rectState]);
    const setRect = useCallback((r) => setRectState(r), []);

    useEffect(() => {
        setPortal(document.getElementById('editor-drawer'));
    }, []);

    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            const savedDocked = readLS(SK.dock, docked);
            const savedMinimized = readLS(SK.min, false);
            const savedFloatRect = readLS(SK.rect, defaultFloatRectRef.current);
            const savedDockWidth = readLS(SK.dockWidth, dockWidth);
            setDocked(savedDocked);
            setMinimized(savedMinimized);
            setFloatRect(savedFloatRect);
            setDockWidth(savedDockWidth);
        }
        wasOpenRef.current = open;
    }, [open, SK.dock, SK.min, SK.rect, SK.dockWidth, docked, dockWidth]);

    useEffect(() => {
        if (!open || !win.w || !win.h) return;

        if (docked) {
            const maxDockWidth = Math.max(MIN_W, win.w - EDGE);
            const width = Math.min(Math.max(MIN_W, dockWidth), maxDockWidth);
            const next = {
                x: win.w - width,
                y: NAVBAR_HEIGHT,
                width,
                height: win.h - NAVBAR_HEIGHT,
            };
            setRectState(next);
        } else {
            const rect = clampFloatRect(
                {
                    ...floatRect,
                    height: minimized ? HEADER_HEIGHT : floatRect.height,
                },
                win.w,
                win.h,
                minimized
            );
            setRectState(rect);
        }
    }, [open, win.w, win.h, docked, minimized, floatRect, dockWidth]);

    useEffect(() => writeLS(SK.dock, docked), [docked, SK.dock]);
    useEffect(() => writeLS(SK.min, minimized), [minimized, SK.min]);

    useEffect(() => {
        if (!open) return;
        if (docked) {
            onDockChange?.(true, rectState.width);
        } else {
            onDockChange?.(false, 0);
        }
    }, [open, docked, rectState.width, onDockChange]);

    useEffect(() => {
        if (!open) return;
        const b = document.body;
        b.style.transition = 'margin-right .2s ease';
        b.style.marginRight = docked ? `${rectState.width}px` : '0px';
        return () => {
            b.style.marginRight = '0px';
        };
    }, [open, docked, rectState.width]);

    const handleDockToggle = useCallback(() => {
        if (!win.w || !win.h) return;
        setMinimized(false);
        setDocked((prev) => {
            const next = !prev;
            if (next) {
                setFloatRect(rectState);
                writeLS(SK.rect, rectState);

                const maxDockWidth = Math.max(MIN_W, win.w - EDGE);
                const width = Math.min(
                    Math.max(MIN_W, rectState.width),
                    maxDockWidth
                );
                setDockWidth(width);
                writeLS(SK.dockWidth, width);
            }
            return next;
        });
    }, [rectState, setFloatRect, setDockWidth, win.w, win.h, SK]);

    const handleClose = useCallback(() => {
        setMinimized(false);
        onClose?.();
    }, [onClose]);

    const handleDragEnd = useCallback(
        (_event, info) => {
            const winW =
                win.w ||
                (typeof window !== 'undefined' ? window.innerWidth : 1);
            const winH =
                win.h ||
                (typeof window !== 'undefined' ? window.innerHeight : 1);

            const dx = info.offset.x;
            const dy = info.offset.y;

            const newX = rectState.x + dx;
            const newY = rectState.y + dy;
            const panelRight = newX + rectState.width;
            const distanceToRight = winW - panelRight;

            if (docked) {
                if (distanceToRight > DETACH_PX) {
                    setDocked(false);
                    const desiredHeight = Math.min(
                        rectState.height,
                        winH * FLOAT_MAX_VH
                    );
                    const newFloatRect = clampFloatRect(
                        {
                            x: newX,
                            y: newY,
                            width: rectState.width,
                            height: desiredHeight,
                        },
                        winW,
                        winH,
                        minimized
                    );
                    setFloatRect(newFloatRect);
                    writeLS(SK.rect, newFloatRect);
                } else {
                    const snapped = {
                        x: winW - rectState.width,
                        y: NAVBAR_HEIGHT,
                        width: rectState.width,
                        height: winH - NAVBAR_HEIGHT,
                    };
                    setRectState(snapped);
                }
                return;
            }

            if (distanceToRight <= SNAP_PX) {
                setDocked(true);
                const maxDockWidth = Math.max(MIN_W, winW - EDGE);
                const width = Math.min(
                    Math.max(MIN_W, rectState.width),
                    maxDockWidth
                );
                setDockWidth(width);
                writeLS(SK.dockWidth, width);
                return;
            }

            const x = Math.min(
                Math.max(newX, EDGE),
                Math.max(EDGE, winW - rectState.width - EDGE)
            );
            const y = Math.min(
                Math.max(newY, NAVBAR_HEIGHT + EDGE),
                Math.max(NAVBAR_HEIGHT + EDGE, winH - rectState.height - EDGE)
            );
            const newFloatRect = { ...rectState, x, y };
            setFloatRect(newFloatRect);
            writeLS(SK.rect, newFloatRect);
        },
        [
            docked,
            rectState,
            win.w,
            win.h,
            minimized,
            setDocked,
            setFloatRect,
            setDockWidth,
            SK,
        ]
    );

    const onResizeEnd = useCallback(() => {
        if (docked) {
            setDockWidth(rectState.width);
            writeLS(SK.dockWidth, rectState.width);
        } else {
            setFloatRect(rectState);
            writeLS(SK.rect, rectState);
        }
    }, [docked, rectState, setDockWidth, setFloatRect, SK]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, handleClose]);

    const handleResizeDown = useResize({
        getRect,
        setRect,
        docked,
        minimized,
        onResizeEnd,
    });

    const dragConstraints = useMemo(
        () => ({
            top: NAVBAR_HEIGHT + EDGE,
            left: EDGE,
            right: Math.max(EDGE, (win.w || 1) - rectState.width - EDGE),
            bottom: Math.max(
                NAVBAR_HEIGHT + EDGE,
                (win.h || 1) - rectState.height - EDGE
            ),
        }),
        [win.w, win.h, rectState.width, rectState.height]
    );

    const dockedDragConstraints = useMemo(
        () => ({
            top: NAVBAR_HEIGHT,
            bottom: NAVBAR_HEIGHT,
            left: EDGE,
            right: Math.max(EDGE, (win.w || 1) - rectState.width),
        }),
        [win.w, rectState.width]
    );

    if (!portal) return null;

    const cornerHandleClass =
        'group absolute h-6 w-6 pointer-events-auto flex items-center justify-center z-50';

    const cornerSvgClass =
        'pointer-events-none absolute inset-0 h-full w-full text-neutral-700 stroke-current opacity-0 transition-opacity duration-200 group-hover:opacity-50';

    return createPortal(
        <AnimatePresence>
            {open && (
                <div className="pointer-events-none fixed inset-0 z-[10001]">
                    <motion.div
                        className="pointer-events-auto relative flex h-full flex-col bg-white/90 shadow-2xl ring-1 ring-black/10 backdrop-blur-xl"
                        style={{
                            position: 'absolute',
                            width: rectState.width,
                            height: docked
                                ? Math.max(1, (win.h || 1) - NAVBAR_HEIGHT)
                                : minimized
                                ? HEADER_HEIGHT
                                : rectState.height,
                            borderRadius: docked ? '0' : '1rem',
                        }}
                        initial={{
                            x: docked ? win.w || 1 : rectState.x,
                            y: docked ? NAVBAR_HEIGHT : rectState.y,
                            opacity: 0,
                        }}
                        animate={{
                            x: rectState.x,
                            y: rectState.y,
                            opacity: 1,
                            transition: {
                                opacity: { duration: 0.2 },
                                x: { duration: 0 },
                                y: { duration: 0 },
                            },
                        }}
                        exit={{
                            opacity: 0,
                            transition: { duration: 0.15 },
                        }}
                        drag
                        dragMomentum={false}
                        dragElastic={0}
                        dragConstraints={
                            docked ? dockedDragConstraints : dragConstraints
                        }
                        onDragEnd={handleDragEnd}
                    >
                        {/* Wrapper for content clipping */}
                        <div
                            className={[
                                'flex h-full w-full flex-col overflow-hidden',
                                docked ? 'rounded-none' : 'rounded-2xl',
                                minimized ? '' : 'divide-y divide-white/10',
                            ].join(' ')}
                        >
                            {/* Header */}
                            <div
                                className={[
                                    'cursor-move select-none bg-gradient-to-r from-primary-700 to-secondary-700',
                                    minimized
                                        ? 'flex h-[60px] items-center px-6'
                                        : 'p-4 sm:px-6',
                                ].join(' ')}
                            >
                                <div className="flex w-full items-center justify-between">
                                    <h2 className="text-base font-semibold leading-6 text-white">
                                        {title}
                                    </h2>
                                    <div className="ml-3 flex h-7 items-center gap-2">
                                        {!docked && (
                                            <button
                                                className="rounded-md text-white hover:opacity-75"
                                                onClick={() =>
                                                    setMinimized((m) => !m)
                                                }
                                            >
                                                <MinusIcon className="h-6 w-6" />
                                            </button>
                                        )}
                                        <button
                                            className="rounded-md text-white hover:opacity-75"
                                            onClick={handleDockToggle}
                                            title={
                                                docked
                                                    ? 'Undock to float'
                                                    : 'Dock to side'
                                            }
                                        >
                                            <DockIcon
                                                className="h-5 w-5"
                                                docked={docked}
                                            />
                                        </button>
                                        <button
                                            className="rounded-md text-white hover:opacity-75"
                                            onClick={handleClose}
                                        >
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Body + footer */}
                            {!minimized && (
                                <>
                                    <div
                                        className="relative flex-1 overflow-y-auto px-4"
                                        onPointerDownCapture={(e) =>
                                            e.stopPropagation()
                                        }
                                        ref={bodyRef}
                                    >
                                        {children}
                                    </div>
                                    {!hideFooter && (
                                        <div
                                            className="flex shrink-0 justify-end bg-gradient-to-r from-primary-700 to-secondary-700 px-6 py-3"
                                            onPointerDownCapture={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <button
                                                type="button"
                                                className="inline-flex items-center justify-center rounded-4xl border border-white bg-white/15 px-3 py-2 text-sm font-semibold text-white hover:bg-white/25"
                                                onClick={() =>
                                                    onCancel
                                                        ? onCancel()
                                                        : handleClose()
                                                }
                                            >
                                                <XMarkIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="ml-4 inline-flex items-center justify-center rounded-4xl border border-white bg-white/15 px-3 py-2 text-sm font-semibold text-white hover:bg-white/25"
                                                onClick={onSave}
                                            >
                                                <CheckIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                                Save
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Docked: edge pull bar */}
                        {docked && (
                            <div
                                onPointerDownCapture={(e) => {
                                    e.stopPropagation();
                                    handleResizeDown('w')(e);
                                }}
                                className="group absolute inset-y-0 left-0 flex w-8 cursor-ew-resize items-center"
                                style={{ touchAction: 'none' }}
                            >
                                <div className="pointer-events-none ml-0.5 h-16 w-[4px] rounded-full bg-neutral-700/20 shadow-sm group-hover:bg-neutral-700/50" />
                            </div>
                        )}

                        {/* Floating: corners */}
                        {!docked && !minimized && (
                            <>
                                <div
                                    onPointerDownCapture={(e) => {
                                        e.stopPropagation();
                                        handleResizeDown('se')(e);
                                    }}
                                    className={`${cornerHandleClass} bottom-0 right-0 cursor-nwse-resize`}
                                    style={{ touchAction: 'none' }}
                                    title="Resize"
                                >
                                    <svg
                                        className={cornerSvgClass}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1.5 22.5h6.5a14.5 14.5 0 0 0 14.5-14.5V1.5" />
                                    </svg>
                                </div>

                                <div
                                    onPointerDownCapture={(e) => {
                                        e.stopPropagation();
                                        handleResizeDown('sw')(e);
                                    }}
                                    className={`${cornerHandleClass} bottom-0 left-0 cursor-nesw-resize`}
                                    style={{ touchAction: 'none' }}
                                    title="Resize"
                                >
                                    <svg
                                        className={cornerSvgClass}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22.5 22.5h-6.5A14.5 14.5 0 0 1 1.5 8V1.5" />
                                    </svg>
                                </div>

                                <div
                                    onPointerDownCapture={(e) => {
                                        e.stopPropagation();
                                        handleResizeDown('ne')(e);
                                    }}
                                    className={`${cornerHandleClass} right-0 top-0 cursor-nesw-resize`}
                                    style={{ touchAction: 'none' }}
                                    title="Resize"
                                >
                                    <svg
                                        className={cornerSvgClass}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1.5 1.5h6.5A14.5 14.5 0 0 1 22.5 16V22.5" />
                                    </svg>
                                </div>

                                <div
                                    onPointerDownCapture={(e) => {
                                        e.stopPropagation();
                                        handleResizeDown('nw')(e);
                                    }}
                                    className={`${cornerHandleClass} left-0 top-0 cursor-nwse-resize`}
                                    style={{ touchAction: 'none' }}
                                    title="Resize"
                                >
                                    <svg
                                        className={cornerSvgClass}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22.5 1.5h-6.5A14.5 14.5 0 0 0 1.5 16V22.5" />
                                    </svg>
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        portal
    );
}
